import React from 'react';
import PropTypes from 'prop-types';
import useIntl from '@afosto/utils-react/intl/useIntl';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Adjustment from '../../../components/Adjustment';
import FiltersRenderer from '../../../components/FiltersRenderer';
import DataGridPreview from '@afosto/app-components-react/DataGrid/DataGridPreview';
import Filter from '@afosto/app-components-react/Filter';
import LoadingWrapper from '@afosto/components-react/LoadingWrapper';
import Property from '@afosto/app-components-react/Property';
import camelize from '@afosto/utils/string/camelize';
import { pricing as pricingApi } from '@afosto/order/api/Pricing';
import {
  boolean as booleanFormatter,
  datetime as datetimeFormatter,
} from '@afosto/app-components-react/DataGrid/formatters';
import constraintsRenderFormats from '../../../data/constraintsRenderFormats';
import filtersRenderFormats from '../../../data/filtersRenderFormats';
import translations from './translations';

const PricingRulePreview = ({
  pricingRule,
  isOpen,
  isLoading,
  previewMenu,
  onClose,
  onDeactivatePricingRule,
}) => {
  const history = useHistory();
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();

  const handleDeactivedPricingRule = async () => {
    try {
      const response = await pricingApi.updatePricingRule(pricingRule.id, {
        ...pricingRule,
        expires_at: new Date(),
      });
      const { data } = response;

      enqueueSnackbar(intl.formatMessage(translations.feedback.pricingRuleDeactivated), {
        variant: 'success',
      });

      if (onDeactivatePricingRule) {
        await onDeactivatePricingRule(data);
      }
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.pricingRuleNotDeactivated), {
        variant: 'error',
      });
    }
  };

  const checkExpiredDate = () => {
    const currentDate = new Date();
    const expiredDate = new Date(pricingRule.expires_at);

    return currentDate.getTime() > expiredDate.getTime();
  };

  const isExpired = pricingRule?.expires_at ? checkExpiredDate() : false;
  const menu = [
    ...(previewMenu || []),
    ...(pricingRule
      ? [
          {
            key: 'edit',
            label: intl.formatMessage(translations.actions.edit),
            icon: 'pencil',
            shortcut: true,
            onClick: () => history.push(`/pricing-rules/edit/${pricingRule.id}`),
          },
          {
            key: 'duplicate',
            label: intl.formatMessage(translations.actions.duplicate),
            icon: 'clone',
            onClick: () => history.push(`/pricing-rules/new?id=${pricingRule.id}`),
          },
        ]
      : []),
    ...(pricingRule && !isExpired
      ? [
          {
            key: 'deactivate',
            label: intl.formatMessage(translations.actions.deactivate),
            icon: 'ban',
            color: 'secondary',
            onClick: handleDeactivedPricingRule,
          },
        ]
      : []),
  ];

  return (
    <DataGridPreview title={pricingRule?.name ?? ''} menu={menu} open={isOpen} onClose={onClose}>
      <LoadingWrapper isLoading={isLoading} style={{ minHeight: 200 }}>
        <Box px={2}>
          <Property
            label={intl.formatMessage(translations.properties.name)}
            value={pricingRule?.name}
          />

          <Property
            label={intl.formatMessage(translations.properties.applyTo)}
            value={
              pricingRule?.apply_to
                ? intl.formatMessage(translations.applyToOptions[camelize(pricingRule.apply_to)])
                : null
            }
          />

          <Property
            label={intl.formatMessage(translations.properties.grantLimit)}
            value={pricingRule?.grant_limit}
          />

          <Property
            label={intl.formatMessage(translations.properties.priority)}
            value={pricingRule?.priority}
          />

          <Property
            label={intl.formatMessage(translations.properties.activeAt)}
            value={pricingRule?.active_at ? datetimeFormatter(pricingRule.active_at) : null}
          />

          <Property
            label={intl.formatMessage(translations.properties.expiresAt)}
            value={pricingRule?.expires_at ? datetimeFormatter(pricingRule.expires_at) : null}
          />

          <Property
            label={intl.formatMessage(translations.properties.isLastRule)}
            value={booleanFormatter(pricingRule?.is_last_rule)}
          />

          {pricingRule?.adjustments?.length > 0 && (
            <>
              <Box mt={4}>
                <Typography variant="overline" style={{ fontWeight: 400, fontSize: 14 }}>
                  <FormattedMessage {...translations.properties.discounts} />
                </Typography>
              </Box>

              {(pricingRule?.adjustments ?? []).map(adjustment => (
                <Adjustment key={adjustment.reference} adjustment={adjustment} />
              ))}
            </>
          )}

          {pricingRule?.constraints?.length > 0 && (
            <>
              <Box mt={4}>
                <Typography variant="overline" style={{ fontWeight: 400, fontSize: 14 }}>
                  <FormattedMessage {...translations.properties.restrictions} />
                </Typography>
              </Box>

              {(pricingRule?.constraints ?? []).map(constraint => (
                <Filter
                  key={constraint?.key}
                  filter={{ ...constraint, type: constraintsRenderFormats[constraint.key] }}
                  translationKey="pricingRuleForm.constraintTypes"
                />
              ))}
            </>
          )}

          {pricingRule?.filters?.length > 0 && (
            <>
              <Box mt={4}>
                <Typography variant="overline" style={{ fontWeight: 400, fontSize: 14 }}>
                  <FormattedMessage {...translations.properties.conditions} />
                </Typography>
              </Box>

              {(pricingRule?.filters ?? []).map(filter => (
                <Filter
                  key={filter?.key}
                  filter={{ ...filter, type: filtersRenderFormats[filter.key] }}
                  translationKey="pricingRuleForm.filterTypes"
                  renderers={{
                    filters: FiltersRenderer,
                  }}
                />
              ))}
            </>
          )}
        </Box>
      </LoadingWrapper>
    </DataGridPreview>
  );
};

PricingRulePreview.propTypes = {
  pricingRule: PropTypes.object,
  isOpen: PropTypes.bool,
  isLoading: PropTypes.bool,
  previewMenu: PropTypes.array,
  onClose: PropTypes.func,
  onDeactivatePricingRule: PropTypes.func,
};

PricingRulePreview.defaultProps = {
  pricingRule: null,
  isOpen: false,
  isLoading: false,
  previewMenu: [],
  onClose: undefined,
  onDeactivatePricingRule: undefined,
};

export default PricingRulePreview;
